import { verifierChampsDEcran } from './validation-champs.js';
import { champsDeSource, champsDeCondition, enTetesDeLaPorte, type SourceSimple } from '@taki/data-model';
import { champsNatifs, estCarteDeChamps, schemaDeFamille } from '@taki/data-model';
import { bindingsDe, champsDuJeu, triDe } from '@taki/ui';
import { definirAppDeclaree, type AppDeclaree } from './app-declaree.js';
import { accesDuJeu } from './acces.js';
import { qualifierLesNatifs } from './natifs.js';
import { effetsCites, portesCanvas } from './noms-cites.js';
import { clefDeSurfaceDeVue } from './vues-de-fiche-donnees.js';
import { resoudreDeclaration, type DeclarationInstance } from './resolution.js';

/** Rejoue le montage de toutes les portes avant l'enregistrement, sans lire ni écrire de données. */
export function verifierInstance(app: AppDeclaree, instance: DeclarationInstance): void {
  const resolution = resoudreDeclaration(app, instance);
  if (resolution.raison) throw new Error(resolution.raison);
  const { champs, ecrans } = resolution.declaration;
  /* L'ecran que l'ecran generique montera : type par le socle (`natifs.ts`). */
  const ecran = qualifierLesNatifs(resolution.declaration.ecran);
  verifierChampsDEcran(champs, ecran);
  const effets = new Set(effetsCites(ecran).map((effet) => effet.nom));
  /* LES SURFACES QUE L'ECRAN MONTE : celles des portes Canvas, et les
     composants `vue:<id>` des vues Canvas qu'il decrit (niveaux Vue et Fiche).
     Sans eux, toute personnalisation d'une app qui livre une vue Canvas
     (Projets, Timesheet) etait refusee a l'enregistrement : « l'app ne fournit
     aucun composant canvas['vue:<id>'] ». */
  const canvas = new Set([...portesCanvas(ecran), ...(ecran.vuesCustom ?? []).map((vue) => clefDeSurfaceDeVue(vue.id))]);
  // Une instance peut retirer un usage, le code livré du modèle reste disponible.
  definirAppDeclaree({
    ...app,
    /* Une App Canvas n'expose aucune declaration (`declarationDEntree`) : ce
       rejeu ne vise que les apps a portes. */
    appCanvas: undefined,
    champs,
    ecrans,
    effets: Object.fromEntries(Object.entries(app.effets).filter(([id]) => effets.has(id))),
    canvas: Object.fromEntries(Object.entries(app.canvas).filter(([id]) => canvas.has(id))),
  });
  const arrivee = ecran.portes.find((p) => p.id === ecran.porteArrivee)?.jeu ?? ecran.portes[0]?.jeu;
  const champsDe = (id: string) => {
    const jeu = ecran.portes.find((p) => p.id === id)?.jeu ?? arrivee;
    return champsDuJeu(champs, jeu?.family ?? '', jeu?.type);
  };
  for (const jeu of champs.jeux) champsNatifs(jeu.family);
  const lecture = accesDuJeu(null, ecran.colonnes ?? []);
  triDe(ecran, champs.jeux.flatMap((jeu) => jeu.champs));
  for (const porte of ecran.portes) {
    for (const lien of porte.liens ?? []) {
      const cible = ecran.portes.find((candidate) => candidate.id === lien.porte);
      const jeuLie = cible?.jeu ?? arrivee;
      if (!cible || !jeuLie) throw new Error(`Le lien « ${lien.id} » vise une porte sans jeu de données.`);
      const natifsLies = schemaDeFamille(jeuLie.family).shape;
      if (!(lien.par in natifsLies) && !champsDe(cible.id).some((champ) => champ.key === lien.par)) {
        throw new Error(`Le lien « ${lien.id} » cite le champ « ${lien.par} », absent du jeu de la porte « ${cible.label} ».`);
      }
    }
    const jeu = porte.jeu ?? arrivee;
    if (jeu) {
      const natifs = schemaDeFamille(jeu.family).shape;
      const declares = new Set(champsDe(porte.id).map((champ) => champ.key));
      const verifierChamp = (nom: string): void => {
        const existe = nom.startsWith('socle:')
          ? nom.slice(6) in natifs
          : declares.has(nom) || nom in natifs;
        if (!existe) throw new Error(`La porte « ${porte.label} » cite le champ « ${nom} », absent de son jeu de données.`);
      };
      for (const filtre of porte.filtres?.autres ?? []) if (filtre.condition) champsDeCondition(filtre.condition).forEach(verifierChamp);
      if (porte.tranche) champsDeCondition(porte.tranche).forEach(verifierChamp);
      for (const { verrous = [] } of enTetesDeLaPorte(porte)) for (const verrou of verrous) champsDeCondition(verrou.si).forEach(verifierChamp);
      for (const binding of Object.values(porte.vb ?? ecran.vb)) {
        if (!binding) continue;
        for (const regle of binding.surbrillances ?? []) if (regle.si !== 'nouveau') champsDeCondition(regle.si).forEach(verifierChamp);
        for (const [clef, valeur] of Object.entries(binding)) {
          if (clef === 'colonnes') (valeur as string[]).forEach(verifierChamp);
          else if (valeur && typeof valeur === 'object' && !Array.isArray(valeur)) {
            const source = valeur as SourceSimple;
            champsDeSource(source).forEach(verifierChamp);
          }
        }
        for (const carte of binding.cartes ?? []) {
          /* Une carte liee ou celle des fichiers ne cite aucun champ de CE jeu. */
          if (!estCarteDeChamps(carte)) continue;
          for (const champ of carte.champs) {
            if (typeof champ === 'string') verifierChamp(champ);
            else if (!('total' in champ)) verifierChamp(champ.champ);
          }
        }
      }
    }
    bindingsDe(ecran, champsDe(porte.id), lecture, app.fonctions, porte.id, champsDe);
  }
}
